import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Stack, IconButton } from "@altimateai/lego";
import { DismissibleAlertBanner } from "@/components/banners/DismissibleAlertBanner";
import { PermissionsEnum } from "@/helpers/constants";
import { useAppState } from "@/modules/app/useAppContext";
import MasksView from "@/pages/security/MasksView";
import { Card, CardContent, Typography, CardHeader, Button, Badge } from "@altimateai/lego";
import { PrivacySelector } from "@/components/form/PrivacySelector";
import { Link } from "react-router-dom";
import { OpenInNewTabIcon } from "@/assets/icons";
import { default as RotateIcon } from "@/assets/icons/rotate.svg?react";
import { getKnowledgeBaseSources } from "@/helpers/apis";
import { KnowledgeCatalogItem } from "@/pages/knowledgeCatalog/types";
import useMCPContext from "../useMCPContext";
import { RelativeComponentLoader } from "@/components/loader";
import KnowledgeCatalogCard from "@/pages/knowledgeCatalog/KnowledgeCatalogCard";
import { useTenantInfo } from "@/helpers/hooks";
import { Privacy } from "@/helpers/constants";
import { PrivacyLevel } from "@/types";
import KnowledgeEngineToggle from "@/pages/teammate/dataMates/knowledgeEngine/KnowledgeEngineToggle";

const Guardrails = () => {
  const { loggedinUser: user } = useAppState();
  const userHasPermission = user?.permission?.includes(PermissionsEnum.SECURITY_VIEW);
  const { enableAdvancedGuardRails, enableKnowledgeEngine } = useTenantInfo();

  // Knowledge Hub logic
  const { teammate, updateTeammate } = useMCPContext();
  const [selectedItems, setSelectedItems] = useState<number[]>(teammate?.knowledge_bases || []);

  const {
    data: knowledgeSources,
    isLoading: isKnowledgeSourcesLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["knowledgeSources"],
    queryFn: () => getKnowledgeBaseSources(),
  });

  // Initialize selection from teammate data (for editing existing datamates)
  useEffect(() => {
    if (teammate?.knowledge_bases) {
      setSelectedItems(teammate.knowledge_bases);
    }
  }, [teammate?.knowledge_bases]);

  // Update teammate when selection changes
  useEffect(() => {
    updateTeammate({
      knowledge_bases: selectedItems,
    });
  }, [selectedItems, updateTeammate]);

  const handleSelectAll = () => {
    if (knowledgeSources) {
      setSelectedItems(knowledgeSources.map(item => item.id));
    }
  };

  const handleSelectNone = () => {
    setSelectedItems([]);
  };

  const handleItemToggle = (itemId: number) => {
    setSelectedItems(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const handleRefresh = () => {
    refetch();
  };

  // Privacy logic
  const handlePrivacyChange = (value: PrivacyLevel) => {
    updateTeammate({
      privacy: value,
    });
  };

  const isAllSelected = knowledgeSources && selectedItems.length === knowledgeSources.length;
  const isNoneSelected = selectedItems.length === 0;

  const renderKnowledgeHubContent = () => {
    if (isKnowledgeSourcesLoading) {
      return <RelativeComponentLoader label="Loading knowledge sources..." componentHeight={15} />;
    }

    if (error) {
      return (
        <Stack justify="center" align="center">
          <Typography variant="body" className="al-text-red-600">
            Error loading knowledge hub items. Please try again.
          </Typography>
        </Stack>
      );
    }

    if (!knowledgeSources || knowledgeSources.length === 0) {
      return (
        <Stack justify="center" align="center" direction="column" className="al-gap-4">
          <Typography variant="h4">No Knowledge Hub Items Found</Typography>
          <Typography variant="body" className="al-text-center">
            You don&apos;t have any knowledge hub items yet. Create some knowledge sources first, or
            your datamate will work without specific knowledge context.
          </Typography>
          <Button variant="outline" asChild>
            <a href="/knowledge-hub" target="_blank" rel="noopener noreferrer">
              Go to Knowledge Hub
            </a>
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction="column" className="al-gap-4">
        {/* Action buttons */}
        <Stack direction="row" className="al-gap-2">
          <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={isAllSelected}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={handleSelectNone} disabled={isNoneSelected}>
            Select None
          </Button>
        </Stack>

        {/* Default behavior notice */}
        {isNoneSelected && (
          <div className="al-p-4 al-bg-blue-50 al-border al-border-blue-200 al-rounded-md">
            <Typography variant="body" className="al-text-blue-800">
              <strong>Note:</strong> Since no items are selected, your datamate will have access to
              all available knowledge hub items by default.
            </Typography>
          </div>
        )}

        {/* Knowledge items list */}
        <Stack direction="row" className="al-gap-4 !al-flex-wrap">
          {knowledgeSources.map((item: KnowledgeCatalogItem) => (
            <KnowledgeCatalogCard
              key={item.id}
              item={item}
              showCheckbox={true}
              openInNewTab={true}
              isSelected={selectedItems.includes(item.id)}
              onClick={handleItemToggle}
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack gap="lg" direction="column">
      <DismissibleAlertBanner
        bannerId="mcp-guardrails"
        content="Protect sensitive data with custom guardrails. Control what information is shared from your data ecosystem to LLM providers. Prevent PII and confidential data exposure."
      />
      <Card>
        <CardHeader>
          <Stack direction="row" justify="between" align="center">
            <Typography variant="section-heading">Data protection</Typography>
            {userHasPermission && (
              <Button variant="secondary" size="sm" asChild>
                <Link to="/settings/security" target="_blank">
                  Edit
                  <OpenInNewTabIcon />
                </Link>
              </Button>
            )}
          </Stack>
        </CardHeader>
        <CardContent>
          <MasksView />
        </CardContent>
      </Card>

      {/* Privacy Section - Only show if advanced guardrails are enabled */}
      {enableAdvancedGuardRails && (
        <Card>
          <CardHeader>
            <Typography variant="section-heading">Privacy</Typography>
            <Typography variant="body" className="al-text-gray-600 al-mt-1 al-text-sm">
              Control who can access this datamate within your organization
            </Typography>
          </CardHeader>
          <CardContent>
            <PrivacySelector
              value={teammate?.privacy || Privacy.PRIVATE}
              onValueChange={handlePrivacyChange}
              entityType="datamate"
            />
          </CardContent>
        </Card>
      )}

      {/* Knowledge Hub Section - Only show if advanced guardrails are enabled */}
      {enableAdvancedGuardRails && (
        <Card>
          <CardHeader>
            <Stack direction="row" justify="between" align="center">
              <Typography variant="section-heading">Knowledge Hub</Typography>
              <Stack direction="row" align="center" className="al-gap-2">
                <Badge variant="secondary">
                  {selectedItems.length} of {knowledgeSources?.length || 0} selected
                </Badge>
                <IconButton
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={isKnowledgeSourcesLoading}
                  title="Refresh knowledge hub items"
                  className="al-p-2"
                >
                  <RotateIcon className="al-w-4 al-h-4" />
                </IconButton>
              </Stack>
            </Stack>
          </CardHeader>
          <CardContent>
            {/* Knowledge Engine Toggle - Only show if enableKnowledgeEngine flag is true */}
            {enableKnowledgeEngine && (
              <div className="al-mb-4">
                <KnowledgeEngineToggle showInfo={false} />
              </div>
            )}
            <Typography variant="body" className="al-text-gray-600 al-text-sm al-mb-4">
              Choose which knowledge sources your datamate should have access to
            </Typography>
            {renderKnowledgeHubContent()}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default Guardrails;
